<script setup lang="ts">
import { Dropdown, Menu, Tooltip } from "floating-vue";
import { computed, inject, onUnmounted, provide, ref, useAttrs, watch } from "vue";

import { GLOBAL_POPPER_PARENT_ID } from "~/constants/injection";
import { when } from "~/utils/wait";

import {
  getNextPopperId,
  hasChildren,
  registerActivePopper,
  registerChild,
  unregisterActivePopper,
  unregisterChild,
} from "./globalPopper";
import { type GlobalPopperTrigger, PopperType } from "./shared";

const POPPER_COMPONENTS = {
  [PopperType.DROPDOWN]: Dropdown,
  [PopperType.MENU]: Menu,
  [PopperType.TOOLTIP]: Tooltip,
} as const;

const props = withDefaults(
  defineProps<{
    type: PopperType;
    noWrapper?: boolean;
    [key: string]: unknown;
  }>(),
  {}
);

defineOptions({
  inheritAttrs: false,
});
const attrs = useAttrs();

const popperComponent = computed(() => POPPER_COMPONENTS[props.type]);

const isActive = ref(false);
const popperRef = ref<InstanceType<typeof Tooltip | typeof Dropdown | typeof Menu> | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const activeId = ref<number | null>(null);

const parentId = inject(GLOBAL_POPPER_PARENT_ID, null);
provide(GLOBAL_POPPER_PARENT_ID, activeId);

// Break the floating-vue parent-child chain for tooltips so they don't register in the parent
// dropdown's shownChildren, which would block the dropdown from closing while a tooltip is shown.
if (props.type === PopperType.TOOLTIP) {
  provide("__floating-vue__popper", null);
}

const getReferenceNode = () => {
  const refNode = attrs["reference-node"] as () => Element | null;
  return !refNode ? triggerRef.value : (refNode() ?? triggerRef.value);
};

const getTargetNodes = () => {
  if ([PopperType.TOOLTIP, PopperType.MENU].includes(props.type) && triggerRef.value) {
    return [triggerRef.value];
  }
  return [];
};

const deactivate = () => {
  if (!isActive.value) {
    return true;
  }
  if (popperRef.value?.shown || (activeId.value && hasChildren(activeId.value))) {
    return false;
  }

  popperRef.value?.hide();
  if (activeId.value) {
    unregisterChild(activeId.value);
    unregisterActivePopper(props.type, activeId.value);
  }

  isActive.value = false;
  activeId.value = null;
  return true;
};

const activate = () => {
  if (isActive.value) {
    return activeId.value;
  }

  activeId.value = getNextPopperId();
  const parentIdValue = parentId?.value ?? null;

  if (parentIdValue !== null) {
    registerChild(activeId.value, parentIdValue);
  }
  registerActivePopper(props.type, activeId.value, deactivate);
  isActive.value = true;

  return activeId.value;
};

const show = async () => {
  activate();
  await when(() => !!popperRef.value);
  popperRef.value?.show();
};

const hide = () => {
  popperRef.value?.hide();
};

const showPopperIfDropdown = () => {
  if (props.type !== PopperType.DROPDOWN) {
    return;
  }
  show();
};

const setTriggerRef = (e: HTMLElement | null) => {
  triggerRef.value = e;
};

watch(
  () => attrs.shown,
  (shown) => {
    if (!shown || isActive.value) {
      return;
    }
    activate();
  },
  { immediate: true }
);

watch(
  triggerRef,
  (e) => {
    if (!e) {
      return;
    }
    const trigger = e as GlobalPopperTrigger;
    trigger.dataset.globalPopper = "";

    if (Array.isArray(attrs.triggers) && attrs.triggers.length === 0) {
      return;
    }
    trigger.__activatePopper = activate;
    trigger.__showPopper = showPopperIfDropdown;
  },
  { immediate: true }
);

onUnmounted(() => {
  if (activeId.value === null) {
    return;
  }
  unregisterChild(activeId.value);
  unregisterActivePopper(props.type, activeId.value);
});

defineExpose({
  show,
  hide,
  ref: computed(() => popperRef.value),
  triggerRef: computed(() => triggerRef.value),
});
</script>

<template>
  <slot v-if="noWrapper" :trigger-ref="setTriggerRef" />
  <div v-else ref="triggerRef" v-bind="attrs" :class="attrs.class">
    <slot :trigger-ref="setTriggerRef" />
  </div>

  <component
    :is="popperComponent"
    v-if="isActive"
    ref="popperRef"
    :overflow-padding="{ top: 14, right: 14, bottom: 14, left: 14 }"
    v-bind="{ ...attrs, class: '' }"
    :reference-node="() => getReferenceNode()"
    :target-nodes="() => getTargetNodes()"
    class="contents"
    @apply-hide="deactivate">
    <template #popper>
      <slot name="popper" />
    </template>
  </component>
</template>
